import { TVacationScheduleInfo } from 'shared/types/vacation-schedule.types';
import { edpApiV1 } from './index';

export type EmployeesRequest = {
  search?: string;
  size?: number;
  page?: number;
  year: number;
  divisionId?: string;
  filterRequestStatuses?: string[];
  filterEmployees?: string;
  allAvailableOrganizations: boolean;
};

export type EmployeesResponse = {
  data: TVacationScheduleInfo[];
  pagination: {
    size: number;
    page: number;
    total: number;
  };
};

export type PermitsResponse = {
  vacationTypeId: string;
  name: string;
  amountDays: number;
}[];

export type EmployeeAddRequest = {
  employeeId: number;
  requests: {
    vacationTypeId: string;
    startedAt: string;
    endedAt: string;
  }[];
};

export type RejectScheduleRequest = {
  employeeId: number;
  requestId: number;
  comment?: string;
};

export type SendToZupCheckResponse = {
  statusCode: number;
  message: string;
  zupRequest: {
    id: number;
    status: string;
  };
};

export const vacationsScheduleApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    allowScheduling: build.mutation<string, null>({
      query: () => ({ url: '/v1/admin/hr/vacation-schedule/allow-scheduling', method: 'POST' }),
      invalidatesTags: ['allowCheckScheduling'],
    }),
    allowCheckScheduling: build.query<unknown, null>({
      query: () => ({
        url: '/v1/admin/hr/vacation-schedule/allow-scheduling/check',
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      providesTags: ['allowCheckScheduling'],
    }),
    downloadSchedule: build.query<string, EmployeesRequest>({
      query: params => ({
        url: '/v1/admin/hr/vacation-schedule/employees/download-file',
        method: 'GET',
        params,
        responseHandler: async response => {
          const link = document.createElement('a');
          link.download = 'График отпусков';
          link.href = window.URL.createObjectURL(await response.blob());
          link.click();
        },
      }),
    }),
    getEmployeesSchedule: build.query<EmployeesResponse, EmployeesRequest>({
      query: params => ({
        url: '/v1/admin/hr/vacation-schedule/employees',
        method: 'GET',
        params,
      }),
      providesTags: ['employeesSchedule'],
    }),
    permits: build.query<PermitsResponse, { employeeId: number }>({
      query: ({ employeeId, ...params }) => ({
        url: `/v1/admin/hr/vacation-schedule/employees/${employeeId}/permits`,
        method: 'GET',
        params,
      }),
    }),
    employeeAddRequest: build.mutation<string, EmployeeAddRequest>({
      query: ({ employeeId, ...body }) => ({
        url: `/v1/admin/hr/vacation-schedule/employees/${employeeId}/requests`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['employeesSchedule'],
    }),
    rejectSchedule: build.mutation<string, RejectScheduleRequest>({
      query: ({ employeeId, requestId, ...body }) => ({
        url: `/v1/admin/hr/vacation-schedule/employees/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['employeesSchedule'],
    }),
    cancelMarkToSending: build.mutation<string, { employeeId: number }>({
      query: ({ employeeId, ...params }) => ({
        url: `/v1/admin/hr/vacation-schedule/employees/${employeeId}/requests/cancel-approve`,
        method: 'PATCH',
        params,
      }),
      invalidatesTags: ['employeesSchedule'],
    }),
    markToSending: build.mutation<string, { employeeId: number }>({
      query: ({ employeeId, ...params }) => ({
        url: `/v1/admin/hr/vacation-schedule/employees/${employeeId}/requests/approve`,
        method: 'PATCH',
        params,
      }),
      invalidatesTags: ['employeesSchedule'],
    }),
    approveSeveralEmployeesVS: build.mutation<string, { employeeIds: number[] }>({
      query: ({ employeeIds }) => ({
        url: `/v1/admin/hr/vacation-schedule/employees/requests/approve`,
        method: 'PATCH',
        body: { employeeIds },
      }),
      invalidatesTags: ['employeesSchedule'],
    }),
    sendToZup: build.mutation<string, null>({
      query: () => ({
        url: '/v1/admin/hr/vacation-schedule/send-to-zup',
        method: 'POST',
      }),
    }),
    sendToZupCheck: build.query<SendToZupCheckResponse, null>({
      query: () => ({
        url: '/v1/admin/hr/vacation-schedule/send-to-zup/check',
        method: 'GET',
        pollingInterval: 6000,
      }),
      providesTags: ['sendToZupCheck'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEmployeesScheduleQuery,
  useLazyGetEmployeesScheduleQuery,
  useAllowSchedulingMutation,
  useRejectScheduleMutation,
  useCancelMarkToSendingMutation,
  useMarkToSendingMutation,
  useSendToZupCheckQuery,
  useAllowCheckSchedulingQuery,
  useEmployeeAddRequestMutation,
  useLazyDownloadScheduleQuery,
  useLazySendToZupCheckQuery,
  usePermitsQuery,
  useLazyPermitsQuery,
  useSendToZupMutation,
  useApproveSeveralEmployeesVSMutation,
} = vacationsScheduleApi;
